import { useState, useRef, useEffect } from "preact/hooks"
import { UserState } from "@/auth/user"
import { ChatInput } from "./ChatInput"
import { MessageList } from "./MessageList"
import { QuickActions } from "./QuickActions"
import { ConversationHistory } from "./ConversationHistory"
import { IntelligenceBrief } from "./IntelligenceBrief"
import { AttachedDeduction } from "./DeductionAttach"
import { useTypewriter } from "./useTypewriter"
import {
  createConversation,
  getMessages,
  sendMessageStream,
  WizardMessage,
  WizardStreamCallbacks,
  ConfirmationAction,
} from "@/api/wizard"
import { Wand2, History } from "lucide-react"
import { Redirect } from "wouter-preact"
import { DEFAULT_LOGGED_IN_PATH } from "@/app/route"
import { get_display_name } from "@/utils"

export interface DisplayMessage {
  id: string
  role: "user" | "assistant"
  content: string
  thinking?: string
  toolCalls?: ToolCallDisplay[]
  confirmationAction?: ConfirmationAction
  /** Attached deduction context (user messages) */
  attachedDeduction?: { id: string; invoice_number?: string; invoice_amount?: number; source?: string }
  /** Names of files uploaded with this message */
  uploadedFiles?: string[]
}

export interface ToolCallDisplay {
  toolName: string
  args: any
  result?: any
  confirmationRequired?: boolean
  confirmationAction?: ConfirmationAction
  status: "calling" | "done"
}

export function WizardPage() {
  const user = UserState.use()

  // Only system_admin users can access the Wizard
  if (!user || user.role !== "system_admin") {
    return <Redirect to={DEFAULT_LOGGED_IN_PATH} />
  }

  const [conversationId, setConversationId] = useState<string | null>(null)
  const [messages, setMessages] = useState<DisplayMessage[]>([])
  const [isStreaming, setIsStreaming] = useState(false)
  const [thinkingMessage, setThinkingMessage] = useState<string | null>(null)
  const [streamingThinking, setStreamingThinking] = useState<string>("")
  const [isThinkingPhase, setIsThinkingPhase] = useState(false)
  const [streamingText, setStreamingText] = useState<string>("")
  const [activeToolCalls, setActiveToolCalls] = useState<ToolCallDisplay[]>([])
  const [showHistory, setShowHistory] = useState(false)
  const [pendingPrompt, setPendingPrompt] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  const firstName = get_display_name(user) || "there"

  function getGreeting(): string {
    const hour = new Date().getHours()
    if (hour < 12) return "Good morning"
    if (hour < 17) return "Good afternoon"
    return "Good evening"
  }

  async function handleSend(content: string, attachedDeduction?: AttachedDeduction, files?: File[]) {
    if (isStreaming) return

    let activeConversationId = conversationId
    if (!activeConversationId) {
      const conversation = await createConversation()
      activeConversationId = conversation.id
      setConversationId(activeConversationId)
    }

    // Add user message (with attached deduction info and file names for display)
    const userMsg: DisplayMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      content,
      attachedDeduction: attachedDeduction || undefined,
      uploadedFiles: files?.map((f: File) => f.name),
    }
    setMessages((prev: DisplayMessage[]) => [...prev, userMsg])
    setIsStreaming(true)
    setThinkingMessage("Thinking...")
    setStreamingThinking("")
    setIsThinkingPhase(false)
    setStreamingText("")
    setActiveToolCalls([])

    let assistantContent = ""
    let accumulatedThinking = ""
    const toolCalls: ToolCallDisplay[] = []
    let pendingConfirmation: ConfirmationAction | undefined

    const callbacks: WizardStreamCallbacks = {
      onThinking(message: string) {
        setThinkingMessage(message)
      },
      onThinkingDelta(content: string) {
        accumulatedThinking += content
        setStreamingThinking(accumulatedThinking)
        setIsThinkingPhase(true)
        setThinkingMessage(null)
      },
      onThinkingDone() {
        setIsThinkingPhase(false)
      },
      onToolCall(toolName: string, args: any) {
        const tc: ToolCallDisplay = { toolName, args, status: "calling" }
        toolCalls.push(tc)
        setActiveToolCalls([...toolCalls])
        setThinkingMessage(`Running ${formatToolName(toolName)}...`)
      },
      onToolResult(toolName: string, result: any, confirmationRequired: boolean, confirmationAction?: ConfirmationAction) {
        const tc = toolCalls.find((t: ToolCallDisplay) => t.toolName === toolName && t.status === "calling")
        if (tc) {
          tc.result = result
          tc.status = "done"
          tc.confirmationRequired = confirmationRequired
          tc.confirmationAction = confirmationAction
        }
        if (confirmationRequired && confirmationAction) {
          pendingConfirmation = confirmationAction
        }
        setActiveToolCalls([...toolCalls])
      },
      onTextDelta(content: string) {
        assistantContent += content
        setStreamingText(assistantContent)
        setThinkingMessage(null)
      },
      onText(content: string) {
        assistantContent = content
        setStreamingText(content)
        setThinkingMessage(null)
      },
      onDone() {
        const assistantMsg: DisplayMessage = {
          id: `assistant-${Date.now()}`,
          role: "assistant",
          content: assistantContent,
          thinking: accumulatedThinking || undefined,
          toolCalls: toolCalls.length > 0 ? [...toolCalls] : undefined,
          confirmationAction: pendingConfirmation,
        }
        setMessages((prev: DisplayMessage[]) => [...prev, assistantMsg])
        setIsStreaming(false)
        setThinkingMessage(null)
        setStreamingThinking("")
        setIsThinkingPhase(false)
        setStreamingText("")
        setActiveToolCalls([])
      },
      onError(error: string) {
        const errorMsg: DisplayMessage = {
          id: `error-${Date.now()}`,
          role: "assistant",
          content: `An error occurred: ${error}`,
        }
        setMessages((prev: DisplayMessage[]) => [...prev, errorMsg])
        setIsStreaming(false)
        setThinkingMessage(null)
        setStreamingThinking("")
        setIsThinkingPhase(false)
        setStreamingText("")
        setActiveToolCalls([])
      },
    }

    abortRef.current = sendMessageStream(activeConversationId, content, callbacks, {
      deductionId: attachedDeduction?.id,
      files,
    })
  }

  async function handleSelectConversation(id: string) {
    setConversationId(id)
    setShowHistory(false)
    const msgs = await getMessages(id)
    const displayMsgs: DisplayMessage[] = msgs
      .filter((m: WizardMessage) => m.role !== "tool")
      .map((m: WizardMessage) => {
        // Reconstruct tool call displays by merging tool_calls and tool_results
        let toolCalls: ToolCallDisplay[] | undefined
        if (m.tool_calls) {
          const calls = Array.isArray(m.tool_calls) ? m.tool_calls : []
          const results = Array.isArray(m.tool_results) ? m.tool_results : []
          toolCalls = calls.map((tc: any, i: number) => {
            const tr = results[i] || {}
            return {
              toolName: tc.name,
              args: tc.args,
              result: tr.result,
              confirmationRequired: tr.confirmation_required || false,
              confirmationAction: tr.confirmation_action,
              status: "done" as const,
            }
          })
        }
        // Find the last confirmation action if any
        const lastConfirmation = toolCalls?.find(
          (tc: ToolCallDisplay) => tc.confirmationRequired && tc.confirmationAction,
        )?.confirmationAction

        // Extract thinking text from metadata (assistant messages)
        const thinking = m.metadata?.thinking_text || undefined

        // Extract attached deduction from metadata (user messages)
        const attachedDeductionId = m.metadata?.attached_deduction_id
        const attachedDeduction = attachedDeductionId
          ? { id: attachedDeductionId }
          : undefined

        return {
          id: m.id,
          role: m.role as "user" | "assistant",
          content: m.content,
          thinking,
          toolCalls,
          confirmationAction: lastConfirmation,
          attachedDeduction,
        }
      })
    setMessages(displayMsgs)
  }

  function handleNewConversation() {
    setConversationId(null)
    setMessages([])
    setShowHistory(false)
  }

  function handleQuickAction(prompt: string) {
    setPendingPrompt(prompt)
  }

  // Smooth out thinking token bursts with a typewriter animation
  const { displayedText: displayedThinking, isAnimating: thinkingAnimating } = useTypewriter(streamingThinking)

  const hasMessages = messages.length > 0

  return (
    <div class="flex h-full">
      {showHistory && (
        <ConversationHistory
          onSelect={handleSelectConversation}
          onNew={handleNewConversation}
          onClose={() => setShowHistory(false)}
          activeId={conversationId}
        />
      )}
      <div class="flex-1 flex flex-col h-full overflow-hidden">
        {!hasMessages ? (
          <div class="flex-1 flex flex-col items-center justify-center px-4">
            <div class="flex items-center gap-3 mb-8">
              <div class="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                <Wand2 className="w-5 h-5" style={{ color: "white" }} />
              </div>
              <h1 class="text-3xl font-semibold text-gray-800">
                {getGreeting()}, {firstName}.
              </h1>
            </div>

            <div class="w-full max-w-2xl mb-6">
              <ChatInput
                onSend={handleSend}
                isStreaming={isStreaming}
                onShowHistory={() => setShowHistory(true)}
                pendingPrompt={pendingPrompt}
                onPendingPromptConsumed={() => setPendingPrompt(null)}
              />
            </div>

            <QuickActions onAction={handleQuickAction} />

            <div class="mt-16 w-full max-w-3xl">
              <IntelligenceBrief />
            </div>
          </div>
        ) : (
          <>
            <div class="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <div class="flex items-center gap-2">
                <button
                  onClick={() => setShowHistory(!showHistory)}
                  class="p-2 rounded-lg hover:bg-gray-100 text-gray-500"
                >
                  <History className="w-5 h-5" />
                </button>
                <button
                  onClick={handleNewConversation}
                  class="text-sm text-primary hover:opacity-80 font-medium"
                >
                  New conversation
                </button>
              </div>
              <div class="flex items-center gap-2">
                <Wand2 className="w-4 h-4 text-primary" />
                <span class="text-sm font-medium text-gray-600">MarginWiz</span>
              </div>
            </div>

            <MessageList
              messages={messages}
              isStreaming={isStreaming}
              thinkingMessage={thinkingMessage}
              streamingThinking={displayedThinking}
              isThinkingPhase={isThinkingPhase || thinkingAnimating}
              streamingText={streamingText}
              activeToolCalls={activeToolCalls}
              conversationId={conversationId}
            />

            <div class="px-4 py-3 border-t border-gray-200">
              <ChatInput
                onSend={handleSend}
                isStreaming={isStreaming}
                onShowHistory={() => setShowHistory(true)}
              />
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function formatToolName(name: string): string {
  return name
    .replace(/_/g, " ")
    .replace(/\b\w/g, (l: string) => l.toUpperCase())
}
